import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { Address } from '../types';
import { Trash2, Edit2, CheckCircle, Plus, MapPin } from 'lucide-react';
import { notify } from '../lib/toast';
import AddressForm from './AddressForm';

interface AddressListProps {
    selectable?: boolean;
    selectedId?: string;
    onSelect?: (address: Address) => void;
}

export default function AddressList({ selectable = false, selectedId, onSelect }: AddressListProps) {
    const { currentUser } = useAuth();
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingAddress, setEditingAddress] = useState<Address | undefined>(undefined);

    useEffect(() => {
        if (!currentUser) return;

        const q = query(
            collection(db, 'users', currentUser.id, 'addresses'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Address[];
            setAddresses(docs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Bu adresi silmek istediğinize emin misiniz?')) return;
        try {
            if (!currentUser) return;
            await deleteDoc(doc(db, 'users', currentUser.id, 'addresses', id));
            notify.success('Adres silindi.');
        } catch (error) {
            console.error(error);
            notify.error('Adres silinemedi.');
        }
    };

    const handleSetDefault = async (address: Address) => {
        if (!currentUser) return;
        try {
            const batch = writeBatch(db);

            // Set all others to non-default
            addresses.forEach(addr => {
                if (addr.isDefault) {
                    const ref = doc(db, 'users', currentUser.id, 'addresses', addr.id);
                    batch.update(ref, { isDefault: false });
                }
            });

            // Set this one to default
            const targetRef = doc(db, 'users', currentUser.id, 'addresses', address.id);
            batch.update(targetRef, { isDefault: true });

            await batch.commit();
            notify.success('Varsayılan adres güncellendi.');
        } catch (error) {
            console.error(error);
            notify.error('Varsayılan adres değiştirilemedi.');
        }
    };

    if (showForm) {
        return (
            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg border dark:border-white/5 transition-colors duration-300">
                <h3 className="text-lg font-bold mb-4 dark:text-white">{editingAddress ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}</h3>
                <AddressForm
                    initialData={editingAddress}
                    onSuccess={() => {
                        setShowForm(false);
                        setEditingAddress(undefined);
                    }}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingAddress(undefined);
                    }}
                />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold dark:text-white">Adreslerim ({addresses.length})</h3>
                <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-black dark:bg-white text-white dark:text-gray-950 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 text-sm active:scale-95 transition-all"
                >
                    <Plus size={16} />
                    Yeni Adres Ekle
                </button>
            </div>

            {loading ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">Yükleniyor...</div>
            ) : addresses.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-500 dark:text-gray-400 dark:border-white/5">
                    <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p>Henüz kayıtlı adresiniz yok.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addresses.map((address) => (
                        <div
                            key={address.id}
                            className={`
                relative p-4 rounded-xl border-2 transition-all
                ${selectable && selectedId === address.id
                                    ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-white/5 shadow-premium'
                                    : 'border-gray-100 dark:border-white/5 hover:border-gray-300 dark:hover:border-white/10 bg-white dark:bg-gray-900'}
                ${selectable ? 'cursor-pointer' : ''}
              `}
                            onClick={() => selectable && onSelect && onSelect(address)}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-lg dark:text-white">{address.title}</span>
                                    {address.isDefault && (
                                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-xs font-medium rounded-full text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/5">
                                            Varsayılan
                                        </span>
                                    )}
                                </div>
                                {!selectable && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setEditingAddress(address); setShowForm(true); }}
                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDelete(address.id); }}
                                            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full text-red-500"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <p className="font-medium text-gray-900 dark:text-white uppercase tracking-tight">{address.fullName}</p>
                                <p>{address.phone}</p>
                                <p>{address.neighborhood}, {address.street}</p>
                                <p>No: {address.buildingNo}, Daire: {address.apartmentNo}</p>
                                <p>{address.district} / {address.city}</p>
                            </div>

                            {!selectable && !address.isDefault && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleSetDefault(address); }}
                                    className="mt-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline transition-colors"
                                >
                                    Varsayılan Yap
                                </button>
                            )}

                            {selectable && selectedId === address.id && (
                                <div className="absolute top-4 right-4 text-emerald-500 dark:text-white">
                                    <CheckCircle size={24} fill="currentColor" fillOpacity={0.1} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
